"use client";

import { GrowthBook } from "@growthbook/growthbook-react";
import type { TrackingCallback } from "@growthbook/growthbook-react";

import { logger } from "@/core/logging";

import { resolveGrowthBookClientKey } from "@/core/featureFlags/growthbook.config";
import type { FeatureFlagAttributes } from "@/core/featureFlags/types";

export type CreateGrowthBookClientOptions = {
  readonly attributes?: FeatureFlagAttributes;
  /**
   * Leave unset until exposures may be reported: a client without a tracking callback
   * holds every exposure itself and replays them when `setTrackingCallback` is called.
   */
  readonly trackingCallback?: TrackingCallback;
};

/**
 * Creates a new GrowthBook client instance
 * This should be called once per client session (not at module scope)
 *
 * @returns A new GrowthBook instance configured for the client
 */
export function createGrowthBookClient(
  options?: CreateGrowthBookClientOptions,
): GrowthBook<FeatureFlagAttributes> {
  const clientKey = resolveGrowthBookClientKey();

  if (!clientKey) {
    logger.warn("growthbook_client_key_missing");
  }

  return new GrowthBook<FeatureFlagAttributes>({
    apiHost: "https://cdn.growthbook.io",
    clientKey: clientKey ?? "",
    enableDevMode: process.env.NODE_ENV !== "production",
    subscribeToChanges: true,
    attributes: options?.attributes,
    trackingCallback: options?.trackingCallback,
  });
}
